import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { numberFormat } from '../../../helpers/utils';
import { formatCurrencyExtended } from '../../../helpers/currencyFormatter';
import {
  GraphTitle,
  GraphTitleOperation,
  HalfPageModule,
  SectionTitle,
} from '../../../styles/global.styles';
import {
  AssetInfoContainer,
  AssetInfoWrapper,
  AssetPercentiles,
  AssetValueLabel,
  AssetValueMark,
  AssetValuePriceLabel,
  Bar,
  BarGraph,
  Dot,
  Line,
  MaxLabel,
  MedianContainer,
  MedianLegend,
  MiddleLabel,
  MiddleLabelText,
  MiddleMark,
  MinLabel,
  OutlierContainer,
  OutlierLabel,
  XAxis,
  XAxisLabel,
  YAxis,
  YAxisLabel,
} from './assetValueOnMarket.styles';
import {
  HistogramDataBucket,
  HistogramDataModel,
} from '../../../types/indicators.interfaces';
import { ReactComponent as WarningIcon } from '../../../assets/images/warning.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/images/redArrow.svg';

export const AssetValueOnMarket = (props: HistogramDataModel & { country?: string }) => {
  const [searchParams] = useSearchParams();
  const intl = useIntl();
  const listingType = Number(searchParams.get('listing_type'));
  const country = props.country || 'es';

  const buckets =
    props.buckets?.sort((a, b) => (a.value ?? 0) - (b.value ?? 0)) ?? []; // Sorting buckets by value
  const values = buckets.map((item) => item.value ?? 0);

  const formattedBuckets = addPercMinMax(buckets);
  const mainValue = props.assetValue;
  const totalUnits = Number(
    buckets.reduce((sum, item) => sum + (item.units || 0), 0).toFixed(0)
  );

  // Calculate the original percentages
  const originalPercentages = buckets.map((item) => {
    if (item.units) {
      return Number(
        ((Number(item.units) / Number(totalUnits)) * 100).toFixed(2)
      );
    } else {
      return 0;
    }
  });

  const maxPercentage = Math.max(...originalPercentages);

  const barHeights = calculateBarHeights(buckets, maxPercentage);
  const aimedValue = calculatePercentage(formattedBuckets, mainValue);
  const medianPosition = calculatePercentage(
    formattedBuckets,
    props.averagePrice ?? 0
  );

  // Get translated suffixes
  const sqmSuffix = intl.formatMessage({ id: 'sqmPriceSuffix' });
  const monthSuffix = intl.formatMessage({ id: 'rentPriceSuffix' });

  // Function to format price based on operation and listing type
  const formatAssetPrice = (value: number) => {
    if (listingType === 7 || listingType === 11) {
      return formatCurrencyExtended(value, country, false, sqmSuffix).replace(/\s/g, '');
    } else if (props.operation === 1) {
      return formatCurrencyExtended(value, country, true, sqmSuffix).replace(/\s/g, '');
    } else {
      return formatCurrencyExtended(value, country, false, sqmSuffix, monthSuffix).replace(/\s/g, '');
    }
  };

  return (
    <>
      {props && aimedValue?.percentage && (
        <HalfPageModule>
          <SectionTitle>
            <FormattedMessage id="marketGraph.title" />
          </SectionTitle>
          <AssetPercentiles>
            <GraphTitle>
              <FormattedMessage
                id="marketGraph.subtitle"
                values={{
                  operation: (
                    <GraphTitleOperation
                      color={props.operation == 1 ? `#775175` : `#CC7D34`}
                    >
                      {props.operation == 1 ? (
                        <FormattedMessage id="marketGraph.subtitleOperationSale" />
                      ) : (
                        <FormattedMessage id="marketGraph.subtitleOperationRent" />
                      )}
                    </GraphTitleOperation>
                  ),
                }}
              />
            </GraphTitle>
            <BarGraph>
              <YAxis>
                {generatePercentageProgression(maxPercentage)
                  .reverse()
                  .map((label, index) => (
                    <div
                      style={{
                        width: '100%',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                      key={index}
                    >
                      <YAxisLabel key={index}>
                        <span>{label}%</span>
                      </YAxisLabel>
                      <Line key={index}></Line>
                    </div>
                  ))}
              </YAxis>
              <XAxis>
                <MiddleLabel style={{ left: `${medianPosition?.percentage}%` }}>
                  <MiddleLabelText>
                    <FormattedMessage id="median" />
                  </MiddleLabelText>
                  <MiddleMark />
                </MiddleLabel>
                {buckets.map((item, index) => {
                  if (item.bucket !== 0) {
                    if (item.units !== null) {
                      return (
                        <XAxisLabel key={index}>
                          <Bar
                            color={getBarColor(
                              aimedValue?.bucketNumber === item.bucket,
                              props.operation
                            )}
                            height={barHeights ? barHeights[index] : 0}
                            highlighted={Boolean(
                              aimedValue?.bucketNumber === item.bucket
                            ).toString()}
                          ></Bar>
                        </XAxisLabel>
                      );
                    } else {
                      return (
                        <XAxisLabel key={index}>
                          <Bar
                            height={0}
                            highlighted={Boolean(
                              aimedValue?.bucketNumber === item.bucket
                            ).toString()}
                          />
                        </XAxisLabel>
                      );
                    }
                  }
                })}
                <AssetValueMark
                  left={aimedValue?.percentage ?? 0}
                  isOutOfRange={Boolean(mainValue > Math.max(...values))}
                  isBelowRange={Boolean(mainValue < Math.min(...values))}
                >
                  <AssetInfoWrapper
                    isOutOfRange={Boolean(mainValue > Math.max(...values))}
                    isBelowRange={Boolean(mainValue < Math.min(...values))}
                  >
                    <AssetInfoContainer>
                      <AssetValueLabel
                        isOutOfRange={Boolean(mainValue > Math.max(...values))}
                        isBelowRange={Boolean(mainValue < Math.min(...values))}
                      >
                        <FormattedMessage id="marketGraph.yourAsset" />
                      </AssetValueLabel>
                      <AssetValuePriceLabel
                        isOutOfRange={Boolean(mainValue > Math.max(...values))}
                        isBelowRange={Boolean(mainValue < Math.min(...values))}
                      >
                        {formatAssetPrice(Number(mainValue.toFixed(0)))}
                      </AssetValuePriceLabel>
                    </AssetInfoContainer>
                    {Boolean(
                      mainValue < Math.min(...values) ||
                        mainValue > Math.max(...values)
                    ) && (
                      <OutlierContainer
                        isOutOfRange={Boolean(mainValue > Math.max(...values))}
                        isBelowRange={Boolean(mainValue < Math.min(...values))}
                      >
                        <OutlierLabel>
                          <WarningIcon />
                          <FormattedMessage id="marketGraph.outlier" />
                        </OutlierLabel>
                        <ArrowIcon
                          style={{
                            transform:
                              mainValue < Math.min(...values)
                                ? 'rotate(180deg)'
                                : 'none',
                          }}
                        />
                      </OutlierContainer>
                    )}
                  </AssetInfoWrapper>
                  {Array.from({ length: 40 }, (_, i) => `Item ${i + 1}`).map(
                    (item) => (
                      <Dot
                        key={item}
                        color={
                          Boolean(
                            mainValue < Math.min(...values) ||
                              mainValue > Math.max(...values)
                          )
                            ? '#943939'
                            : '#0c1b7a'
                        }
                      />
                    )
                  )}
                </AssetValueMark>
              </XAxis>
              <MinLabel>
                <FormattedMessage id="min" />{' '}
                {formatAssetPrice(Math.min(...values))}
              </MinLabel>
              <MaxLabel>
                <FormattedMessage id="max" />{' '}
                {formatAssetPrice(Math.max(...values))}
              </MaxLabel>
              <MedianContainer>
                <MedianLegend>
                  <FormattedMessage id="median" />{' '}
                  {formatAssetPrice(props.averagePrice ?? 0)}
                </MedianLegend>
              </MedianContainer>
            </BarGraph>
          </AssetPercentiles>
        </HalfPageModule>
      )}
    </>
  );
};

function addPercMinMax(buckets: HistogramDataModel['buckets']) {
  let percMin = 6;
  let percMax = percMin + 8;

  buckets?.forEach((item, index) => {
    item.percMin = percMin;
    item.percMax = percMax;

    percMin = percMax + 2;
    percMax = percMin + 8;
  });

  return buckets;
}

type Result = {
  bucketNumber: number;
  percentage: number;
};

function calculatePercentage(
  buckets: HistogramDataBucket[] | null,
  targetValue: number
): Result | null {
  if (!buckets || buckets.length === 0) {
    return null;
  }

  // Handle case where target value is less than the first bucket
  if (targetValue < buckets[0].value!) {
    return {
      bucketNumber: buckets[0].bucket,
      percentage: buckets[0].percMin || 0,
    };
  }

  // Handle case where target value is greater than the last bucket
  if (targetValue >= buckets[buckets.length - 1].value!) {
    return {
      bucketNumber: buckets[buckets.length - 1].bucket,
      percentage: buckets[buckets.length - 1].percMax || 100,
    };
  }

  for (let i = 1; i < buckets.length; i++) {
    const currentBucket = buckets[i];
    const previousBucket = buckets[i - 1];

    if (
      previousBucket?.value &&
      currentBucket?.value &&
      targetValue >= previousBucket.value &&
      targetValue < currentBucket.value
    ) {
      const rangeMin = previousBucket.value;
      const rangeMax = currentBucket.value;
      const range = rangeMax - rangeMin;
      const positionInRange = targetValue - rangeMin;

      if (previousBucket.percMax && previousBucket.percMin) {
        const percentage =
          (positionInRange *
            (previousBucket.percMax - previousBucket.percMin)) /
            range +
          previousBucket.percMin;

        return {
          bucketNumber: currentBucket.bucket,
          percentage: percentage,
        };
      }
    }
  }

  return null;
}

function getBarColor(isHighlighted: boolean, operation: number) {
  if (isHighlighted) {
    return operation == 1 ? `#775175` : `#CC7D34`;
  } else {
    return `#B6BBC6`;
  }
}

function calculateBarHeights(
  buckets: HistogramDataModel['buckets'],
  maxHeight: number
) {
  const max = Math.ceil(maxHeight / 5) * 5;
  return buckets?.map((item) => {
    if (item.units) {
      const heightPercentage = (Number(item.units) / max) * 100;
      return heightPercentage;
    } else {
      return 1;
    }
  });
}

function generatePercentageProgression(maxHeightPercentage: number) {
  const max = Math.ceil(maxHeightPercentage / 5) * 5;

  const progression = [];
  for (let i = 0; i <= max; i += 5) {
    progression.push(i);
  }
  return progression;
}
